// Settings
// ---------
// Single-page settings modal for an existing web application.
// New Application wizard uses separate step components (NewApplication.tsx).

import {
  EMPTY_STRING,
  MAX_LENGTH_USERNAME,
  MAX_LENGTH_PASSPHRASE,
  MAX_LENGTH_TOTP_SECRET,
  MAX_LENGTH_LINK_SCOPE,
  MAX_LENGTH_SECURITY_HEADER_NAME,
  MAX_LENGTH_SECURITY_HEADER_VALUE,
  MAX_LENGTH_APP_URL,
  MAX_LENGTH_API_DOMAINS,
  REQUEST_RATE_TIERS,
  resolveRequestRateTierValue,
} from "@/constants/definitions";
import { deriveWebAppScope, buildApiDomainScopeItems } from "@/utils/pentestScope";
import { validateAndCanonicaliseAppUrl } from "@/utils/appUrlValidation";
import TestUserClass from "@/models/testUser";
import WebApplicationClass from "@/models/webApplication";
import { isWebAppTestRunning } from "@/utils/liveTestState";
import { setSecurityHeaderForWebapp } from "@/services/dataListOperations.service";
import React, { useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { AlertTriangle, Gauge, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import PathScopeExamples from "@/components/common/PathScopeExamples";
import ConfirmModal from "@/components/common/ConfirmModal";
import SortSelect from "@/components/common/SortSelect";
import { useModalAccessibility } from "@/utils/useModalAccessibility";

// Allow the following chars in the allowed/denied links fields
const URL_WHITELIST_CHARS = /^[\s,;:A-Za-z0-9\-./]+$/;

const ERROR_INVALID_CHARS_ALLOWED = "Invalid characters in the exceptions list";
const ERROR_INVALID_CHARS_DENIED  = "Invalid characters in the denied links list";

// Web App Scope (domain) editing. API domains are host + optional base path
// (e.g. "api.example.com/rest") or full URLs, so the split must NOT break on
// ":" or "/". Mirrors PentestScope (Add-Application wizard). Validation is
// deliberately light — the backend canonicalises each entry and is the sole
// authority on scope.
const API_DOMAINS_WHITELIST_CHARS = /^[\sA-Za-z0-9\-._:/,;]+$/;
const API_DOMAINS_SPLIT_CHARS = /[\s,;]+/;
const ERROR_INVALID_CHARS_API_DOMAINS = "Invalid characters in the API domains list";
const SCOPE_UPDATED_MESSAGE =
  "Domains updated. Verify ownership of the new domains to enable testing.";

// Split on whitespace, comma, semicolon, colon
const ARRAY_SPLIT_CHARS = /[\s,;:]+/;

interface ShowSettingsProps {
  webapp: WebApplicationClass;
  show: boolean;
  subscriptionClient: any;
  close: (...args: any[]) => void;
}

function buildLinkList(raw: string): string[] {
  return raw.split(ARRAY_SPLIT_CHARS).filter(Boolean);
}

function ShowSettings({ webapp, show, subscriptionClient, close: closeProp }: ShowSettingsProps) {
  const account1 = webapp.testAccounts?.[0] ?? new TestUserClass();
  const account2 = webapp.testAccounts?.[1] ?? new TestUserClass();

  // Read-only Web App Scope display. Both are shown as bare domains (no
  // protocol) so the web-app and API entries read consistently, and both
  // are derived exactly as the backend derives scope — never from the raw
  // stored URL, which may be a deep link (e.g. ".../#/login") rather than a
  // domain. deriveWebAppScope collapses apex/www to the registrable domain
  // (www.example.com and example.com both -> example.com) while keeping a
  // specific subdomain as-is (app.example.com -> app.example.com).
  const webAppScope = deriveWebAppScope(webapp.url);
  const webAppScopeDomain = webAppScope
    ? webAppScope.mode === "all-subdomains"
      ? webAppScope.registrableDomain
      : webAppScope.host
    : webapp.url; // fallback: unparseable host — show what we have
  // Configured API domains rendered as canonical "host" / "host/path"
  // labels (deduped, protocol stripped). apiDomains is set in the Add
  // Application wizard, not here.
  const apiDomainLabels: string[] = buildApiDomainScopeItems(webapp.apiDomains).map(
    (item) => item.label,
  );

  // Web App Scope editing is permitted ONLY while ownership is unverified.
  // Once verified, the domain boundary is locked (a verified app must never be
  // re-pointed at an unverified domain — see WebApplication.updateDomains); the
  // section falls back to the read-only display + "add a new application" note.
  const isOwnershipVerified = webapp.ownershipVerified;
  const [scopeUrl, setScopeUrl] = useState(webapp.url ?? EMPTY_STRING);
  const [scopeApiDomains, setScopeApiDomains] = useState(
    Array.isArray(webapp.apiDomains)
      ? webapp.apiDomains.join(", ")
      : (webapp.apiDomains as string) || EMPTY_STRING,
  );
  const [scopeError, setScopeError] = useState(EMPTY_STRING);
  const [scopeSuccess, setScopeSuccess] = useState(EMPTY_STRING);
  const [scopeSaving, setScopeSaving] = useState(false);

  // Test accounts
  const [username, setUsername]       = useState(account1.username ?? EMPTY_STRING);
  const [password, setPassword]       = useState(account1.password ?? EMPTY_STRING);
  const [totpSecret, setTotpSecret]   = useState(account1.totpSecret ?? EMPTY_STRING);
  const [usernameAlt, setUsernameAlt] = useState(account2.username ?? EMPTY_STRING);
  const [passwordAlt, setPasswordAlt] = useState(account2.password ?? EMPTY_STRING);
  const [totpSecretAlt, setTotpSecretAlt] = useState(account2.totpSecret ?? EMPTY_STRING);
  const [testAccountsDirty, setTestAccountsDirty] = useState(false);

  // App settings
  const [allowedLinks, setAllowedLinks]         = useState(webapp.allowedLinks ? webapp.allowedLinks.join(", ") : EMPTY_STRING);
  const [deniedLinks, setDeniedLinks]           = useState(webapp.deniedLinks  ? webapp.deniedLinks.join(", ")  : EMPTY_STRING);
  const [requestsPerSecond, setRequestsPerSecond] = useState(
    resolveRequestRateTierValue(webapp.maxRequestsPerSecond)
  );
  const [appSettingsDirty, setAppSettingsDirty] = useState(false);

  // Security header
  const [securityHeaderName, setSecurityHeaderName]         = useState(webapp.securityHeaderName ?? EMPTY_STRING);
  const [securityHeaderValue, setSecurityHeaderValue]       = useState(EMPTY_STRING);
  const [securityHeaderValueSet, setSecurityHeaderValueSet] = useState(webapp.securityHeaderValueSet ?? false);
  const [showSecurityHeaderInput, setShowSecurityHeaderInput] = useState(false);

  // UI
  const [errorMessage, setErrorMessage] = useState(EMPTY_STRING);
  const [saving, setSaving]             = useState(false);
  // Surfaced when the user clicks Save with changes pending AND a test
  // is currently running on this webapp. We accept the save (so they
  // don't lose work) but warn that the settings won't take effect on
  // the in-flight test.
  const [showRunningTestWarning, setShowRunningTestWarning] = useState(false);

  const testAccountSetters: Record<string, React.Dispatch<React.SetStateAction<string>>> = {
    username:      setUsername,
    password:      setPassword,
    totpSecret:    setTotpSecret,
    usernameAlt:   setUsernameAlt,
    passwordAlt:   setPasswordAlt,
    totpSecretAlt: setTotpSecretAlt,
  };

  const appSettingSetters: Record<string, React.Dispatch<React.SetStateAction<string>>> = {
    allowedLinks: setAllowedLinks,
    deniedLinks:  setDeniedLinks,
  };

  const onTestAccountInput = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    const setter = testAccountSetters[name];
    if (setter) setter(value);
    setTestAccountsDirty(true);
  }, []);

  const onAppSettingInput = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    const setter = appSettingSetters[name];
    if (setter) setter(value);
    setAppSettingsDirty(true);
    setErrorMessage(EMPTY_STRING);
  }, []);

  const onLinkScopeBlur = useCallback((event: React.FocusEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    if (!value) return;
    let newErrorMessage = EMPTY_STRING;
    if (name === "allowedLinks" && !URL_WHITELIST_CHARS.test(value)) {
      newErrorMessage = ERROR_INVALID_CHARS_ALLOWED;
    }
    if (name === "deniedLinks" && !URL_WHITELIST_CHARS.test(value)) {
      newErrorMessage = ERROR_INVALID_CHARS_DENIED;
    }
    if (newErrorMessage) setErrorMessage(newErrorMessage);
  }, []);

  const onRequestsPerSecondChange = useCallback((next: string): void => {
    setRequestsPerSecond(parseInt(next, 10));
    setAppSettingsDirty(true);
  }, []);

  // Save an edited Web App Scope (url + API domains). Separate from the batch
  // footer Save because a domain change is a distinct, security-scoped action
  // (it re-arms ownership verification) with its own inline error / success —
  // and the backend rejects it outright once the app is verified. Client-side
  // validation mirrors the Add-Application wizard; the backend re-canonicalises
  // and is authoritative.
  const onSaveDomains = useCallback(async (): Promise<void> => {
    setScopeError(EMPTY_STRING);
    setScopeSuccess(EMPTY_STRING);

    const urlValidation = validateAndCanonicaliseAppUrl(scopeUrl);
    if (urlValidation.error || !urlValidation.canonicalUrl) {
      setScopeError(urlValidation.error ?? EMPTY_STRING);
      return;
    }

    if (scopeApiDomains && !API_DOMAINS_WHITELIST_CHARS.test(scopeApiDomains)) {
      setScopeError(ERROR_INVALID_CHARS_API_DOMAINS);
      return;
    }
    const apiDomainList = scopeApiDomains
      .split(API_DOMAINS_SPLIT_CHARS)
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

    setScopeSaving(true);
    try {
      await webapp.updateDomains(urlValidation.canonicalUrl, apiDomainList);
      // Resync the inputs to the canonical values the backend stored.
      setScopeUrl(webapp.url);
      setScopeApiDomains(
        Array.isArray(webapp.apiDomains) ? webapp.apiDomains.join(", ") : EMPTY_STRING,
      );
      setScopeSuccess(SCOPE_UPDATED_MESSAGE);
    } catch (domainsError: any) {
      setScopeError(domainsError?.message || "Failed to update domains");
    } finally {
      setScopeSaving(false);
    }
  }, [scopeUrl, scopeApiDomains, webapp]);

  // Reset all local state to the webapp's saved values — called on cancel/close
  // so unsaved changes don't persist in the UI.
  const resetToSaved = useCallback((): void => {
    const savedAccount1 = webapp.testAccounts?.[0] ?? new TestUserClass();
    const savedAccount2 = webapp.testAccounts?.[1] ?? new TestUserClass();
    setUsername(savedAccount1.username ?? EMPTY_STRING);
    setPassword(savedAccount1.password ?? EMPTY_STRING);
    setTotpSecret(savedAccount1.totpSecret ?? EMPTY_STRING);
    setUsernameAlt(savedAccount2.username ?? EMPTY_STRING);
    setPasswordAlt(savedAccount2.password ?? EMPTY_STRING);
    setTotpSecretAlt(savedAccount2.totpSecret ?? EMPTY_STRING);
    setTestAccountsDirty(false);
    setAllowedLinks(webapp.allowedLinks ? webapp.allowedLinks.join(", ") : EMPTY_STRING);
    setDeniedLinks(webapp.deniedLinks ? webapp.deniedLinks.join(", ") : EMPTY_STRING);
    setRequestsPerSecond(resolveRequestRateTierValue(webapp.maxRequestsPerSecond));
    setAppSettingsDirty(false);
    setSecurityHeaderName(webapp.securityHeaderName ?? EMPTY_STRING);
    setSecurityHeaderValue(EMPTY_STRING);
    setSecurityHeaderValueSet(webapp.securityHeaderValueSet ?? false);
    setShowSecurityHeaderInput(false);
    setErrorMessage(EMPTY_STRING);
    setShowRunningTestWarning(false);
    // Web App Scope edits are saved via their own button; discard any unsaved
    // edits and clear the inline messages on close so a reopen starts clean.
    setScopeUrl(webapp.url ?? EMPTY_STRING);
    setScopeApiDomains(
      Array.isArray(webapp.apiDomains)
        ? webapp.apiDomains.join(", ")
        : (webapp.apiDomains as string) || EMPTY_STRING,
    );
    setScopeError(EMPTY_STRING);
    setScopeSuccess(EMPTY_STRING);
    setScopeSaving(false);
  }, [webapp]);

  const closeModal = useCallback((): void => {
    resetToSaved();
    closeProp(null);
  }, [closeProp, resetToSaved]);

  // The actual save work. Split out from onSave so the running-test
  // warning modal can defer it until the user confirms.
  const performSave = useCallback(async (): Promise<void> => {
    setSaving(true);

    // Save app settings (links + RPS)
    if (appSettingsDirty) {
      webapp.allowedLinks = buildLinkList(allowedLinks);
      webapp.deniedLinks  = buildLinkList(deniedLinks);
      webapp.maxRequestsPerSecond = requestsPerSecond;
      webapp.updateAppSettings();
    }

    // Save test accounts. PIN is no longer surfaced in the UI — pass
    // empty string so the backend record stays consistent. The model
    // field remains for back-compat with existing data.
    if (testAccountsDirty) {
      const newAccounts: TestUserClass[] = [];
      if (username) {
        const primaryAccount = new TestUserClass();
        primaryAccount.setSimpleValues(username, password, EMPTY_STRING, 0, totpSecret.trim());
        newAccounts.push(primaryAccount);
      }
      if (usernameAlt) {
        const secondaryAccount = new TestUserClass();
        secondaryAccount.setSimpleValues(usernameAlt, passwordAlt, EMPTY_STRING, 1, totpSecretAlt.trim());
        newAccounts.push(secondaryAccount);
      }
      await webapp.saveTestAccounts(newAccounts);
    }

    // Save security header if name or value has changed
    const headerNameChanged  = securityHeaderName !== (webapp.securityHeaderName ?? EMPTY_STRING);
    const headerValueChanged = securityHeaderValue !== EMPTY_STRING;
    if (headerNameChanged || headerValueChanged) {
      try {
        const result = await setSecurityHeaderForWebapp(
          webapp.id,
          securityHeaderName || null,
          securityHeaderValue || null
        );
        if (result) {
          // Sync the webapp model so resetToSaved reads the correct values
          webapp.securityHeaderName = securityHeaderName;
          webapp.securityHeaderValueSet = result.securityHeaderValueSet;
          setSecurityHeaderValueSet(result.securityHeaderValueSet);
          setSecurityHeaderValue(EMPTY_STRING);
          setShowSecurityHeaderInput(false);
        }
      } catch (securityHeaderError) {
        console.error("Failed to save security header:", securityHeaderError);
      }
    }

    setSaving(false);
    closeProp(null);
  }, [
    appSettingsDirty, testAccountsDirty,
    allowedLinks, deniedLinks, requestsPerSecond,
    username, password, totpSecret, usernameAlt, passwordAlt, totpSecretAlt,
    securityHeaderName, securityHeaderValue, webapp, closeProp,
  ]);

  // Top-level Save click. If a test is running on this webapp AND the
  // user has pending changes, surface a confirm modal so they know
  // the new settings won't affect the in-flight test. With no pending
  // changes there's nothing to warn about — fall through to the
  // close-modal behavior of performSave().
  const onSave = useCallback(async (): Promise<void> => {
    if (errorMessage) return;
    const headerNameChanged  = securityHeaderName !== (webapp.securityHeaderName ?? EMPTY_STRING);
    const headerValueChanged = securityHeaderValue !== EMPTY_STRING;
    const hasPendingChanges  =
      appSettingsDirty || testAccountsDirty || headerNameChanged || headerValueChanged;
    if (hasPendingChanges && isWebAppTestRunning(webapp)) {
      setShowRunningTestWarning(true);
      return;
    }
    await performSave();
  }, [
    errorMessage, appSettingsDirty, testAccountsDirty,
    securityHeaderName, securityHeaderValue, webapp, performSave,
  ]);

  // Confirmed from the running-test warning — proceed with the save
  // and dismiss the warning.
  const onConfirmSaveDuringRunningTest = useCallback(async (): Promise<void> => {
    setShowRunningTestWarning(false);
    await performSave();
  }, [performSave]);

  // Modal accessibility hook is called unconditionally above the early
  // return so the rules-of-hooks invariant holds. The hook itself
  // gates its effects on the `show` argument.
  //
  // Escape closes via closeProp directly (hard close) rather than
  // closeModal, which would also fire the dirty-form prompt. Backdrop
  // click and the X button still go through closeModal so a user who
  // mouse-dismisses an in-progress edit gets the prompt.
  const dialogContainerRef = useRef<HTMLDivElement>(null);
  useModalAccessibility(show, () => closeProp(null), dialogContainerRef);

  if (!show) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={closeModal}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Modal */}
      <div
        ref={dialogContainerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        className="relative w-full max-w-2xl rounded-xl bg-white shadow-xl flex flex-col max-h-[90vh]"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Settings</h2>
          <button
            type="button"
            onClick={closeModal}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-8">

          {errorMessage && (
            <p className="text-sm text-red-600">{errorMessage}</p>
          )}

          {/* ── Web App Scope ── */}
          {/* Editable while ownership is UNVERIFIED (typo / forgotten-domain
              recovery), read-only once verified. The verified branch also shows
              the "add a new application to change" note — it is only true, and
              only makes sense, once the domains are locked. */}
          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-0.5">Web App Scope</h3>

            {isOwnershipVerified ? (
              <>
                <p className="text-xs text-gray-400 mb-4">
                  The domains in scope for testing. The app and API domains are
                  configured when the application is added.
                </p>
                <div className="flex flex-col gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                      App Domain
                    </p>
                    <p className="text-sm text-gray-700 break-all">{webAppScopeDomain}</p>
                  </div>
                  {apiDomainLabels.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                        API Domains
                      </p>
                      <ul className="flex flex-col gap-0.5">
                        {apiDomainLabels.map((domain) => (
                          <li key={domain} className="text-sm text-gray-700 break-all">
                            {domain}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-4">
                  For security, changing these domains requires adding a new
                  application, and deleting this one if it is no longer needed.
                </p>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-4">
                  The domains in scope for testing. You can correct these until
                  you verify ownership — after verification they are locked.
                </p>
                {scopeError && <p className="text-sm text-red-600 mb-3">{scopeError}</p>}
                {scopeSuccess && <p className="text-sm text-green-600 mb-3">{scopeSuccess}</p>}
                <div className="flex flex-col gap-3">
                  <FormField
                    label="App Domain"
                    htmlFor="scopeUrl"
                    helperText="The primary application URL to test. HTTPS only."
                  >
                    <Input
                      type="text"
                      name="scopeUrl"
                      id="scopeUrl"
                      value={scopeUrl}
                      placeholder="e.g. app.example.com"
                      maxLength={MAX_LENGTH_APP_URL}
                      onChange={(urlChangeEvent) => {
                        setScopeUrl(urlChangeEvent.target.value);
                        setScopeError(EMPTY_STRING);
                        setScopeSuccess(EMPTY_STRING);
                      }}
                    />
                  </FormField>
                  <FormField
                    label="API Domains"
                    htmlFor="scopeApiDomains"
                    helperText="Optional. Host with an optional base path; separate multiple with commas."
                  >
                    <Input
                      type="text"
                      name="scopeApiDomains"
                      id="scopeApiDomains"
                      value={scopeApiDomains}
                      placeholder="e.g. api.example.com/rest, payments.example.com"
                      maxLength={MAX_LENGTH_API_DOMAINS}
                      onChange={(apiDomainsChangeEvent) => {
                        setScopeApiDomains(apiDomainsChangeEvent.target.value);
                        setScopeError(EMPTY_STRING);
                        setScopeSuccess(EMPTY_STRING);
                      }}
                    />
                  </FormField>
                  <div>
                    <button
                      type="button"
                      onClick={onSaveDomains}
                      disabled={scopeSaving}
                      className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
                    >
                      {scopeSaving ? "Saving…" : "Save domains"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>

          <div className="border-t border-gray-100" />

          {/* ── Test Accounts ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-0.5">Test Accounts (Optional)</h3>
            <p className="text-xs text-gray-400 mb-4">
              Optional credentials used to test authenticated areas of your
              application, only needed if you want authenticated testing. Both
              accounts should be at the same privilege level, which lets us
              detect cross-account access issues where one user can see or
              modify another user's data.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-3">
                <p className="text-xs font-medium text-gray-600">Account 1</p>
                <FormField label="Username" htmlFor="username">
                  <Input type="text" name="username" id="username" value={username}
                    maxLength={MAX_LENGTH_USERNAME} onChange={onTestAccountInput} />
                </FormField>
                <FormField label="Passphrase" htmlFor="password">
                  <Input type="password" name="password" id="password" value={password}
                    maxLength={MAX_LENGTH_PASSPHRASE} onChange={onTestAccountInput} />
                </FormField>
                <FormField label="Authenticator Secret" htmlFor="totpSecret">
                  <Input type="password" name="totpSecret" id="totpSecret" value={totpSecret}
                    maxLength={MAX_LENGTH_TOTP_SECRET} onChange={onTestAccountInput} />
                </FormField>
              </div>
              <div className="flex flex-col gap-3">
                <p className="text-xs font-medium text-gray-600">Account 2</p>
                <FormField label="Username" htmlFor="usernameAlt">
                  <Input type="text" name="usernameAlt" id="usernameAlt" value={usernameAlt}
                    maxLength={MAX_LENGTH_USERNAME} onChange={onTestAccountInput} />
                </FormField>
                <FormField label="Passphrase" htmlFor="passwordAlt">
                  <Input type="password" name="passwordAlt" id="passwordAlt" value={passwordAlt}
                    maxLength={MAX_LENGTH_PASSPHRASE} onChange={onTestAccountInput} />
                </FormField>
                <FormField label="Authenticator Secret" htmlFor="totpSecretAlt">
                  <Input type="password" name="totpSecretAlt" id="totpSecretAlt" value={totpSecretAlt}
                    maxLength={MAX_LENGTH_TOTP_SECRET} onChange={onTestAccountInput} />
                </FormField>
              </div>
            </div>
            {/* Duplicate-username warning — the BE collapses identical
                test accounts down to a single account at test time, so
                cross-account checks (the reason for offering two
                accounts in the first place) silently won't run. Warn
                rather than block — the user may genuinely want a
                single-account test, in which case Account 2 should
                just be left empty. Trim + exact case match mirrors
                the BE comparison. */}
            {username.trim().length > 0 &&
              username.trim() === usernameAlt.trim() && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
                  <p>
                    Both test accounts share the same username — only a single
                    account will be used in testing. Use a different username
                    for Account 2 to enable cross-account checks, or leave
                    Account 2 blank.
                  </p>
                </div>
              )}
          </section>

          <div className="border-t border-gray-100" />

          {/* ── Denied Paths / Exceptions ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-0.5">Path Scope</h3>
            <p className="text-xs text-gray-400 mb-4">
              Controls which parts of your application are tested. If left
              unset, we test everything in scope. Use denied paths to exclude
              areas, and exceptions to carve out parts of a denied area that
              should still be tested.
            </p>
            <PathScopeExamples />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Denied paths"
                htmlFor="deniedLinks"
                helperText="Pages and API endpoints containing this path segment will be excluded from testing."
              >
                <Input
                  type="text"
                  name="deniedLinks"
                  id="deniedLinks"
                  placeholder="e.g. admin, delete-account"
                  value={deniedLinks}
                  maxLength={MAX_LENGTH_LINK_SCOPE}
                  onChange={onAppSettingInput}
                />
              </FormField>
              <FormField
                label="Exceptions"
                htmlFor="allowedLinks"
                helperText="Exceptions / carve-outs to denied paths."
              >
                <Input
                  type="text"
                  name="allowedLinks"
                  id="allowedLinks"
                  placeholder="e.g. admin/dashboard"
                  value={allowedLinks}
                  maxLength={MAX_LENGTH_LINK_SCOPE}
                  onChange={onAppSettingInput}
                />
              </FormField>
            </div>
          </section>

          <div className="border-t border-gray-100" />

          {/* ── Request Rate ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-0.5">Request Rate</h3>
            <p className="text-xs text-gray-400 mb-4">
              How hard we drive your application during testing. Lower rates are
              gentler and less likely to trigger rate limits or alerts; higher
              rates complete tests faster. We automatically ease off if your
              application shows signs of strain. Start low if you are unsure.
            </p>
            <FormField label="Request rate" htmlFor="requestsPerSecond" className="w-32">
              <SortSelect
                id="requestsPerSecond"
                value={String(requestsPerSecond)}
                onChange={onRequestsPerSecondChange}
                options={REQUEST_RATE_TIERS.map((tier) => ({
                  value: String(tier.requestsPerSecond),
                  label: tier.label,
                }))}
                ariaLabel="Request rate"
                icon={Gauge}
              />
            </FormField>
          </section>

          <div className="border-t border-gray-100" />

          {/* ── Security Header ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-0.5">Security Header</h3>
            <p className="text-xs text-gray-400 mb-4">
              An optional HTTP header sent with every request during testing. Your application
              can use this to identify test traffic and allow it through WAFs, rate limiters,
              or alert rules without triggering false positives.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Header name" htmlFor="securityHeaderName">
                <Input
                  type="text"
                  name="securityHeaderName"
                  id="securityHeaderName"
                  placeholder="e.g. X-NIMIS-Token"
                  value={securityHeaderName}
                  maxLength={MAX_LENGTH_SECURITY_HEADER_NAME}
                  onChange={(headerNameEvent) =>
                    setSecurityHeaderName(headerNameEvent.target.value)
                  }
                />
              </FormField>
              <FormField label="Header value" htmlFor="securityHeaderValue">
                {securityHeaderValueSet && !showSecurityHeaderInput ? (
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-sm text-gray-400">Configured</span>
                    <button
                      type="button"
                      onClick={() => setShowSecurityHeaderInput(true)}
                      className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSecurityHeaderName(EMPTY_STRING);
                        setSecurityHeaderValue(EMPTY_STRING);
                        setSecurityHeaderValueSet(false);
                        setShowSecurityHeaderInput(false);
                        setAppSettingsDirty(true);
                      }}
                      className="inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 cursor-pointer"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <Input
                    type="password"
                    name="securityHeaderValue"
                    id="securityHeaderValue"
                    placeholder="Encrypted in storage"
                    value={securityHeaderValue}
                    maxLength={MAX_LENGTH_SECURITY_HEADER_VALUE}
                    onChange={(headerValueEvent) =>
                      setSecurityHeaderValue(headerValueEvent.target.value)
                    }
                  />
                )}
              </FormField>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            type="button"
            onClick={closeModal}
            disabled={saving}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !!errorMessage}
            className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Running-test warning. Rendered inside the same portal so it
          stacks above the settings modal. The ConfirmModal itself
          portals to document.body, so DOM-wise it lives at the same
          level as the settings dialog — z-index order falls out of
          render order, with the warning rendered last so it sits on
          top. */}
      <ConfirmModal
        show={showRunningTestWarning}
        title="Test in progress"
        message={
          <>
            <p>
              A test is currently running on this application. Your
              changes will be saved, but they will not apply to the
              test that's currently running.
            </p>
            <p className="mt-2">
              The new settings will take effect the next time you start
              a test.
            </p>
          </>
        }
        confirmLabel="Save Anyway"
        cancelLabel="Cancel"
        onConfirm={onConfirmSaveDuringRunningTest}
        onCancel={() => setShowRunningTestWarning(false)}
      />
    </div>,
    document.body
  );
}

export default ShowSettings;
